import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, Sparkles, Eye, Layout, Download, Edit, Share2, ChevronDown, Crown, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import BioTemplateClassic from './BioTemplateClassic';
import BioTemplateModern from './BioTemplateModern';
import BioTemplateMinimal from './BioTemplateMinimal';
import BioTemplateCreative from './BioTemplateCreative';
import BioTemplateProfessional from './BioTemplateProfessional';
import BioTemplateGradient from './BioTemplateGradient';
import BioTemplateExecutive from './BioTemplateExecutive';
import UpgradeModal from '../UpgradeModal';
import { useSubscriptionLimits } from '../../hooks/useSubscriptionLimits';
import { useAuth } from '../../contexts/AuthContext';
import QRPopupEnhanced from '../QRPopupEnhanced';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const templates = [
  {
    id: 'classic',
    name: 'Classic',
    component: BioTemplateClassic,
    premium: false
  },
  {
    id: 'modern',
    name: 'Modern',
    component: BioTemplateModern,
    premium: false
  },
  {
    id: 'minimal',
    name: 'Minimal',
    component: BioTemplateMinimal,
    premium: false
  },
  {
    id: 'creative',
    name: 'Creative',
    component: BioTemplateCreative,
    premium: false
  },
  {
    id: 'professional',
    name: 'Professional',
    component: BioTemplateProfessional,
    premium: false
  },
  {
    id: 'gradient',
    name: 'Gradient',
    component: BioTemplateGradient,
    premium: false
  },
  {
    id: 'executive',
    name: 'Executive',
    component: BioTemplateExecutive,
    premium: false
  }
];

const BioTemplateSelector = ({ currentTemplate, onTemplateChange, username, card, bioUrl, constructSocialUrl, trackLinkClick, onUpdateProfileImage, onUpdateLogo, isPremium = false }) => {
  const [isChanging, setIsChanging] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const activeTemplateRef = useRef(null);
  const dropdownRef = useRef(null);
  const { tier, isSubscribed } = useSubscriptionLimits();
  const { user } = useAuth();

  // For non-premium users, use Piper Card logo instead of their uploaded logo
  const displayCard = isPremium ? card : {
    ...card,
    companyLogo: '/logo.png' // Use Piper Card logo for free users
  };

  // Track QR download via API
  const trackQRDownload = async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      await fetch(`${API_URL}/api/cards/${username}/qr-download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
    } catch (error) {
      console.error('Error tracking QR download:', error);
    }
  };

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const handleDownload = async () => {
    try {
      setIsCapturing(true);
      
      toast({
        title: "Creating download...",
        description: "Please wait while we prepare your bio template."
      });

      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 300));

      // Get the active template element
      const templateElement = document.getElementById('active-bio-template');
      if (!templateElement) {
        throw new Error('Template not found');
      }

      // Mark container as being captured
      templateElement.setAttribute('data-capturing', 'true');
      
      // Elements to hide during download
      const elementsToHide = [
        // Edit icons
        'label[for*="upload"]',
        'label[for*="image"]',
        'label[for*="logo"]',
        'label[title*="Edit"]',
        'label[title*="edit"]',
        'input[type="file"]',
        '[id*="upload"]',
        '[id*="-image-"]',
        '[id*="-logo-"]',
        '.pencil-icon',
        'svg.lucide-pencil',
        // Tab buttons (Profile, Gallery, Videos)
        '[role="tablist"]',
        '.tab-buttons',
        'button[role="tab"]',
        // Tab navigation container
        '[class*="tabs"]',
        '[class*="tab-header"]',
        // Any element with "Profile", "Gallery", "Videos" tab styling
        'div[class*="flex"][class*="gap"]:has(button:has(svg.lucide-user))',
        'div[class*="flex"][class*="gap"]:has(button:has(svg.lucide-images))',
        'div[class*="flex"][class*="gap"]:has(button:has(svg.lucide-play))'
      ];
      
      const hiddenElements = [];
      const originalStyles = [];
      
      // Hide tab buttons and navigation
      const tabContainer = templateElement.querySelector('div.flex.justify-center.gap-2, div.flex.gap-2.justify-center');
      if (tabContainer && tabContainer.querySelector('button')) {
        hiddenElements.push(tabContainer);
        originalStyles.push({
          display: tabContainer.style.display,
          visibility: tabContainer.style.visibility,
          height: tabContainer.style.height,
          margin: tabContainer.style.margin,
          padding: tabContainer.style.padding
        });
        tabContainer.style.display = 'none';
        tabContainer.style.visibility = 'hidden';
        tabContainer.style.height = '0';
        tabContainer.style.margin = '0';
        tabContainer.style.padding = '0';
      }
      
      // Also hide any buttons that contain User, Images, or Play icons (tab buttons)
      const tabButtons = templateElement.querySelectorAll('button');
      tabButtons.forEach(btn => {
        const hasTabIcon = btn.querySelector('svg.lucide-user, svg.lucide-images, svg.lucide-play');
        const isTabButton = btn.textContent?.includes('Profile') || 
                           btn.textContent?.includes('Gallery') || 
                           btn.textContent?.includes('Videos');
        if (hasTabIcon || isTabButton) {
          const parent = btn.parentElement;
          if (parent && !hiddenElements.includes(parent)) {
            hiddenElements.push(parent);
            originalStyles.push({
              display: parent.style.display,
              visibility: parent.style.visibility
            });
            parent.style.display = 'none';
            parent.style.visibility = 'hidden';
          }
        }
      });
      
      // Hide edit icons
      const editIcons = templateElement.querySelectorAll(elementsToHide.slice(0, 11).join(', '));
      editIcons.forEach(icon => {
        hiddenElements.push(icon);
        originalStyles.push({
          display: icon.style.display,
          visibility: icon.style.visibility,
          opacity: icon.style.opacity
        });
        icon.style.display = 'none';
        icon.style.visibility = 'hidden';
        icon.style.opacity = '0';
      });

      // Wait for DOM to update
      await new Promise(resolve => setTimeout(resolve, 300));

      // Preload all images to avoid CORS issues
      const images = templateElement.querySelectorAll('img');
      await Promise.all(Array.from(images).map(img => {
        return new Promise((resolve) => {
          if (img.complete) {
            resolve();
          } else {
            img.onload = resolve;
            img.onerror = resolve;
          }
        });
      }));

      let dataUrl = null;
      
      // Try html2canvas first (more reliable for complex layouts)
      try {
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(templateElement, {
          scale: 3,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          removeContainer: true,
          imageTimeout: 15000,
          onclone: (clonedDoc, clonedElement) => {
            // Hide tab buttons in cloned element
            const clonedTabButtons = clonedElement.querySelectorAll('button');
            clonedTabButtons.forEach(btn => {
              const hasTabIcon = btn.querySelector('svg.lucide-user, svg.lucide-images, svg.lucide-play');
              if (hasTabIcon) {
                btn.parentElement.style.display = 'none';
              }
            });
            // Ensure images are visible
            const clonedImages = clonedElement.querySelectorAll('img');
            clonedImages.forEach(img => {
              img.style.visibility = 'visible';
              img.style.opacity = '1';
            });
          }
        });
        dataUrl = canvas.toDataURL('image/png', 1.0);
      } catch (html2canvasError) {
        console.warn('html2canvas failed, trying toPng:', html2canvasError);
        
        // Fallback to toPng
        const { toPng } = await import('html-to-image');
        dataUrl = await toPng(templateElement, {
          quality: 1,
          pixelRatio: 3,
          backgroundColor: '#ffffff',
          cacheBust: true,
          skipFonts: true,
          filter: (node) => {
            if (node.tagName === 'LINK' && node.href && 
                (node.href.includes('fonts.googleapis.com') || 
                 node.href.includes('fonts.gstatic.com'))) {
              return false;
            }
            if (node.tagName === 'SCRIPT') return false;
            return true;
          }
        });
      }

      // Restore all hidden elements
      hiddenElements.forEach((element, index) => {
        const styles = originalStyles[index];
        if (styles) {
          Object.keys(styles).forEach(key => {
            element.style[key] = styles[key] || '';
          });
        }
      });
      templateElement.removeAttribute('data-capturing');

      if (!dataUrl) {
        throw new Error('Failed to generate image');
      }

      // Download the image
      const link = document.createElement('a');
      link.href = dataUrl;
      const activeTemplate = templates.find(t => t.id === currentTemplate) || templates[0];
      link.download = `${card.name.replace(/\s+/g, '_')}-${activeTemplate.name}-Bio.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setIsCapturing(false);
      toast({
        title: "Downloaded!",
        description: "Your bio template has been saved."
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      
      // Restore elements on error
      try {
        const templateElement = document.getElementById('active-bio-template');
        if (templateElement) {
          templateElement.removeAttribute('data-capturing');
          // Show all potentially hidden elements
          const allElements = templateElement.querySelectorAll('*');
          allElements.forEach(el => {
            if (el.style.display === 'none' || el.style.visibility === 'hidden') {
              el.style.display = '';
              el.style.visibility = '';
              el.style.opacity = '';
            }
          });
        }
      } catch (restoreError) {
        console.error('Error restoring elements:', restoreError);
      }
      
      setIsCapturing(false);
      toast({
        title: "Error",
        description: "Failed to download template. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleShareQR = async () => {
    // Require authentication
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to download the QR code.",
        variant: "destructive"
      });
      navigate('/');
      return;
    }

    try {
      toast({
        title: "Preparing download...",
        description: "Please wait."
      });

      // Get the QR code element
      const qrElement = document.getElementById('qr-code-share-selector');
      if (!qrElement) {
        throw new Error('QR code element not found. Please try again.');
      }

      let dataUrl;
      
      try {
        // Try html-to-image first
        const { toPng } = await import('html-to-image');
        dataUrl = await toPng(qrElement, {
          quality: 1,
          pixelRatio: 4,
          backgroundColor: '#ffffff',
          skipFonts: true,
          cacheBust: true
        });
      } catch (htmlToImageError) {
        console.error('html-to-image failed, trying canvas fallback:', htmlToImageError);
        
        // Fallback: Use canvas to capture the QR SVG
        const svgElement = qrElement.querySelector('svg');
        if (!svgElement) {
          throw new Error('QR code SVG not found');
        }

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const svgUrl = URL.createObjectURL(svgBlob);

        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = svgUrl;
        });

        canvas.width = 512;
        canvas.height = 512;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        URL.revokeObjectURL(svgUrl);
        dataUrl = canvas.toDataURL('image/png');
      }

      // Convert to blob and download
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Track the download
      await trackQRDownload();

      // Download the image
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${card.name || 'QR'}_Code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast({
        title: "QR Code Downloaded!",
        description: "QR code image has been saved to your device."
      });
    } catch (error) {
      console.error('Error downloading QR:', error);
      toast({
        title: "Download Failed",
        description: error.message || "Failed to download QR code. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDownloadWallpaper = async () => {
    try {
      setIsCapturing(true);
      
      toast({
        title: "Creating wallpaper...",
        description: "Please wait while we prepare your wallpaper."
      });

      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 300));

      // Dynamically import html-to-image
      const { toPng } = await import('html-to-image');

      // Get the preview modal content element
      const previewElement = document.getElementById('preview-template-content');
      if (!previewElement) {
        throw new Error('Preview template not found');
      }

      // Capture the preview template with transparent background - High Resolution
      // Skip fonts to avoid CORS issues in Firefox
      const cardDataUrl = await toPng(previewElement, {
        quality: 1,
        pixelRatio: 4,
        backgroundColor: 'transparent',
        skipFonts: true
      });

      // Create a canvas for the wallpaper - Higher Resolution
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set phone wallpaper dimensions (taller for modern phones - 19.5:9 ratio) - High Resolution
      canvas.width = 2160;  // 2x resolution for sharper display
      canvas.height = 4800; // 2x resolution for sharper display

      // Load the card image first
      const cardImage = new Image();
      cardImage.src = cardDataUrl;
      
      await new Promise((resolve, reject) => {
        cardImage.onload = resolve;
        cardImage.onerror = reject;
      });

      // Calculate scaling to fit card on wallpaper (70% of width for mobile app look)
      const maxWidth = canvas.width * 0.70;
      const scale = maxWidth / cardImage.width;
      const scaledWidth = cardImage.width * scale;
      const scaledHeight = cardImage.height * scale;

      // Center the card on the wallpaper
      const x = (canvas.width - scaledWidth) / 2;
      const y = (canvas.height - scaledHeight) / 2;
      const borderRadius = 40;

      // First, fill entire canvas with black (background outside card)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw white background for the entire card area with rounded corners
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.moveTo(x + borderRadius, y);
      ctx.lineTo(x + scaledWidth - borderRadius, y);
      ctx.quadraticCurveTo(x + scaledWidth, y, x + scaledWidth, y + borderRadius);
      ctx.lineTo(x + scaledWidth, y + scaledHeight - borderRadius);
      ctx.quadraticCurveTo(x + scaledWidth, y + scaledHeight, x + scaledWidth - borderRadius, y + scaledHeight);
      ctx.lineTo(x + borderRadius, y + scaledHeight);
      ctx.quadraticCurveTo(x, y + scaledHeight, x, y + scaledHeight - borderRadius);
      ctx.lineTo(x, y + borderRadius);
      ctx.quadraticCurveTo(x, y, x + borderRadius, y);
      ctx.closePath();
      ctx.fill();

      // Clip to rounded rectangle and draw the card on top
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x + borderRadius, y);
      ctx.lineTo(x + scaledWidth - borderRadius, y);
      ctx.quadraticCurveTo(x + scaledWidth, y, x + scaledWidth, y + borderRadius);
      ctx.lineTo(x + scaledWidth, y + scaledHeight - borderRadius);
      ctx.quadraticCurveTo(x + scaledWidth, y + scaledHeight, x + scaledWidth - borderRadius, y + scaledHeight);
      ctx.lineTo(x + borderRadius, y + scaledHeight);
      ctx.quadraticCurveTo(x, y + scaledHeight, x, y + scaledHeight - borderRadius);
      ctx.lineTo(x, y + borderRadius);
      ctx.quadraticCurveTo(x, y, x + borderRadius, y);
      ctx.closePath();
      ctx.clip();

      // Draw the card on the canvas
      ctx.drawImage(cardImage, x, y, scaledWidth, scaledHeight);
      ctx.restore();

      // Add subtle shadow for depth
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 40;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 15;
      
      // Draw rounded rectangle border for card outline
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(x + borderRadius, y);
      ctx.lineTo(x + scaledWidth - borderRadius, y);
      ctx.quadraticCurveTo(x + scaledWidth, y, x + scaledWidth, y + borderRadius);
      ctx.lineTo(x + scaledWidth, y + scaledHeight - borderRadius);
      ctx.quadraticCurveTo(x + scaledWidth, y + scaledHeight, x + scaledWidth - borderRadius, y + scaledHeight);
      ctx.lineTo(x + borderRadius, y + scaledHeight);
      ctx.quadraticCurveTo(x, y + scaledHeight, x, y + scaledHeight - borderRadius);
      ctx.lineTo(x, y + borderRadius);
      ctx.quadraticCurveTo(x, y, x + borderRadius, y);
      ctx.closePath();
      ctx.stroke();

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const safeName = card?.name ? card.name.replace(/\s+/g, '_') : 'Card';
        const templateName = previewTemplate?.name ? previewTemplate.name.replace(/\s+/g, '_') : 'Template';
        link.download = `${safeName}-${templateName}-Wallpaper.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setIsCapturing(false);
        toast({
          title: "Wallpaper downloaded!",
          description: `${previewTemplate.name} wallpaper is ready.`
        });
      }, 'image/png', 1.0);

    } catch (error) {
      console.error('Error creating wallpaper:', error);
      setIsCapturing(false);
      toast({
        title: "Error",
        description: "Failed to create wallpaper. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Scroll to active template on load
  useEffect(() => {
    // Use timeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (activeTemplateRef.current && carouselRef.current) {
        const activeElement = activeTemplateRef.current;
        const carousel = carouselRef.current;
        
        // Calculate scroll position to center the active template
        const elementLeft = activeElement.offsetLeft;
        const elementWidth = activeElement.offsetWidth;
        const carouselWidth = carousel.offsetWidth;
        const scrollPosition = elementLeft - (carouselWidth / 2) + (elementWidth / 2);
        
        carousel.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Navigation functions - scroll exactly one template at a time
  const scrollLeft = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.offsetWidth;
      carouselRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.offsetWidth;
      carouselRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleSetTemplate = async (templateId, templateName) => {
    if (templateId === currentTemplate) {
      toast({
        title: 'Already Active',
        description: 'This template is already active.',
        variant: 'default'
      });
      return;
    }

    // Check if template is premium and user is on free tier
    const template = templates.find(t => t.id === templateId);
    if (template && template.premium && !isSubscribed) {
      setShowUpgradeModal(true);
      return;
    }
    
    try {
      setIsChanging(true);
      
      // Get Firebase auth token
      const { auth } = await import('../../firebase/config');
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        throw new Error('You must be logged in to change templates');
      }
      
      const token = await currentUser.getIdToken();
      
      // Call API to update template
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${BACKEND_URL}/api/cards/${username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          bioTemplate: templateId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update template');
      }

      toast({
        title: 'Template Updated!',
        description: `Your bio page now uses the ${templateName} template.`
      });

      // Notify parent component - this will update the UI without page reload
      onTemplateChange(templateId);
      
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update template. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <div className="w-full">
      {/* Single Template Display - No top bar, controls moved to bottom panel */}
      <div className="relative w-full mx-auto">
        {(() => {
          const activeTemplate = templates.find(t => t.id === currentTemplate) || templates[0];
          const TemplateComponent = activeTemplate.component;
          
          return (
            <div className="w-full">
              {/* Template Display - Full content, page scrolling */}
              <div className="relative w-full">
                {/* Full Template - Natural height, page handles scrolling */}
                <div className="w-full" id="active-bio-template">
                  <TemplateComponent
                    card={displayCard}
                    bioUrl={bioUrl}
                    constructSocialUrl={constructSocialUrl}
                    trackLinkClick={trackLinkClick}
                    onQRClick={() => setShowQRModal(true)}
                    isOwner={true}
                    onUpdateProfileImage={onUpdateProfileImage}
                    onUpdateLogo={onUpdateLogo}
                    isPremium={isPremium}
                  />
                </div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          onClick={() => setShowPreview(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-amber-500 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                <h3 className="font-bold text-lg">Full Card Image - {previewTemplate.name}</h3>
              </div>
              <div className="flex items-center gap-2">
                {/* Download Wallpaper Button */}
                <button
                  onClick={handleDownloadWallpaper}
                  disabled={isCapturing}
                  className="hover:bg-white/20 rounded-full p-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download as Wallpaper"
                >
                  <Download className="w-5 h-5" />
                </button>
                {/* Close Button */}
                <button
                  onClick={() => setShowPreview(false)}
                  className="hover:bg-white/20 rounded-full p-1 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content - Scrollable Bio Preview */}
            <div id="preview-template-content" className="overflow-y-auto max-h-[calc(90vh-60px)]">
              <previewTemplate.component
                card={displayCard}
                bioUrl={bioUrl}
                constructSocialUrl={constructSocialUrl}
                trackLinkClick={trackLinkClick}
                onQRClick={() => setShowQRModal(true)}
                isPremium={isPremium}
              />
            </div>
          </div>
        </div>
      )}

      {/* Enhanced QR Code Modal with Share & Offline Contact */}
      <QRPopupEnhanced
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        card={displayCard}
        bioUrl={bioUrl}
        qrColor={displayCard.qrColor || '#4F46E5'}
        logoSrc={displayCard.companyLogo || displayCard.profileImage || '/logo.png'}
        isPremium={displayCard.is_premium_card}
        onQRDownload={trackQRDownload}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        feature="premium bio templates"
      />
    </div>
  );
};

export default BioTemplateSelector;
